import { Stack } from 'common/ui/Layout';
import { WorkspaceLayoutPreset } from 'generated/graphql';
import { Colors } from 'styles/index';
import ListItemButton from '@mui/material/ListItemButton';
import { DisplayArrangementIcon } from './DisplayArrangementIcon';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { transparentize } from 'color2k';
import { pluralize } from '@sironamedical/common';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState, useMemo } from 'react';
import { activeWorkspaceLayoutPresetState, workspaceDisplayArrangementState } from '../state';
import { useRecoilValue } from 'recoil';
import { CompatibilityResult, getWorkspaceLayoutPresetCompatibility } from '../utils';
import {
  WorkspaceLayoutMenuChip,
  type WorkspaceLayoutMenuChipVariant,
} from './WorkspaceLayoutMenuChip';
import { useWorkspaceLayoutManager } from '../useWorkspaceLayoutManager';

export const WorkspaceLayoutMenuItemWrapper = ({
  children,
  isDisabled,
  onClick,
}: {
  children: React.ReactElement;
  isDisabled?: boolean;
  onClick?: () => void;
}) => {
  const listItemSx = {
    cursor: isDisabled ? 'not-allowed' : onClick ? 'pointer' : 'default',
    px: 2,
    borderBottom: `1px solid ${Colors.gray5}`,
    flexShrink: 0,
    alignItems: 'center',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    backgroundColor: isDisabled
      ? transparentize(Colors.gray8, 0.9)
      : transparentize(Colors.gray7, 0.9),
    opacity: isDisabled ? 0.5 : 1,

    ...(onClick && !isDisabled
      ? {
          '&:hover': {
            backgroundColor: transparentize(Colors.gray4, 0.1),
          },
        }
      : {}),

    '&:last-child': {
      borderBottom: 'none',
    },
  };

  if (!isDisabled && onClick) {
    return (
      <ListItemButton onClick={onClick} sx={listItemSx}>
        {children}
      </ListItemButton>
    );
  }

  return <ListItem sx={listItemSx}>{children}</ListItem>;
};

export const WorkspaceLayoutMenuItem = ({
  workspaceLayoutPreset,
  onEdit,
  onClone,
  onDelete,
}: {
  workspaceLayoutPreset: Partial<WorkspaceLayoutPreset>;
  onEdit?: (preset: Partial<WorkspaceLayoutPreset>) => void;
  onClone?: (preset: Partial<WorkspaceLayoutPreset>) => void;
  onDelete?: (preset: Partial<WorkspaceLayoutPreset>) => Promise<void>;
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const { selectWorkspaceLayoutPreset } = useWorkspaceLayoutManager();
  const displayCount = workspaceLayoutPreset.displaySlots?.length ?? 0;
  const windowCount = workspaceLayoutPreset.windows?.length ?? 0;
  const currentDisplayArrangement = useRecoilValue(workspaceDisplayArrangementState);
  const activeWorkspaceLayoutPreset = useRecoilValue(activeWorkspaceLayoutPresetState);
  const compatibility = useMemo(
    () =>
      getWorkspaceLayoutPresetCompatibility(
        [...(workspaceLayoutPreset.displaySlots ?? [])],
        currentDisplayArrangement?.displays ?? []
      ),
    [workspaceLayoutPreset.displaySlots, currentDisplayArrangement?.displays]
  );

  const isDefaultLayout = useMemo(() => {
    return (
      (workspaceLayoutPreset.isDefault && compatibility === CompatibilityResult.Exact) ?? false
    );
  }, [workspaceLayoutPreset.isDefault, compatibility]);

  const isActiveLayout = useMemo(() => {
    if (workspaceLayoutPreset.id == null) return false;

    return activeWorkspaceLayoutPreset?.presetId === workspaceLayoutPreset.id;
  }, [activeWorkspaceLayoutPreset?.presetId, workspaceLayoutPreset.id]);

  const canEdit = workspaceLayoutPreset.id != null;
  const isMenuOpen = menuAnchorEl != null;
  const [isDeleting, setIsDeleting] = useState(false);

  const canApplyPreset = useMemo(() => {
    return (
      compatibility !== CompatibilityResult.Incompatible &&
      workspaceLayoutPreset.id != null &&
      workspaceLayoutPreset.name != null &&
      workspaceLayoutPreset.windows != null &&
      workspaceLayoutPreset.displayArrangementKey != null
    );
  }, [
    compatibility,
    workspaceLayoutPreset.id,
    workspaceLayoutPreset.name,
    workspaceLayoutPreset.windows,
    workspaceLayoutPreset.displayArrangementKey,
  ]);

  const statusChipVariant = useMemo<WorkspaceLayoutMenuChipVariant | null>(() => {
    if (compatibility === CompatibilityResult.Incompatible) return 'incompatible';
    if (isActiveLayout) return 'active';
    return null;
  }, [compatibility, isActiveLayout]);

  const layoutName = useMemo(
    () => workspaceLayoutPreset.name ?? `${windowCount} ${pluralize(windowCount, 'window')}`,
    [windowCount, workspaceLayoutPreset.name]
  );

  return (
    <WorkspaceLayoutMenuItemWrapper
      onClick={
        canApplyPreset
          ? () =>
              selectWorkspaceLayoutPreset({
                id: workspaceLayoutPreset.id!,
                name: workspaceLayoutPreset.name!,
                windows: workspaceLayoutPreset.windows!,
                displayArrangementKey: workspaceLayoutPreset.displayArrangementKey!,
              })
          : undefined
      }
      isDisabled={compatibility === CompatibilityResult.Incompatible}
    >
      <Stack alignX="between" alignY="center" space="small" style={{ width: '100%' }}>
        <Stack alignY="center" style={{ minWidth: 0, flex: 1 }}>
          <DisplayArrangementIcon displaySlots={workspaceLayoutPreset.displaySlots} />
          <ListItemText
            primary={layoutName}
            secondary={`${displayCount} ${pluralize(displayCount, 'display')} • ${windowCount} ${pluralize(windowCount, 'window')}`}
            sx={{ minWidth: 0 }}
            slotProps={{
              primary: {
                fontWeight: 500,
                fontSize: 12,
                noWrap: true,
              },
              secondary: {
                mt: 0.25,
                lineHeight: 1.35,
                fontSize: 12,
                noWrap: true,
              },
            }}
          />
        </Stack>
        <Stack alignY="center" space="xsmall">
          <Stack space="xsmall">
            {isDefaultLayout && <WorkspaceLayoutMenuChip variant="default" />}
            {statusChipVariant != null && <WorkspaceLayoutMenuChip variant={statusChipVariant} />}
          </Stack>
          <IconButton
            aria-label="workspace-layout-actions"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              setMenuAnchorEl(event.currentTarget);
            }}
            size="small"
            disabled={!canEdit}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={() => setMenuAnchorEl(null)}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <MenuItem
              disabled={!canEdit}
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchorEl(null);
                onEdit?.(workspaceLayoutPreset);
              }}
            >
              Edit
            </MenuItem>
            <MenuItem
              disabled={!canEdit}
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchorEl(null);
                onClone?.(workspaceLayoutPreset);
              }}
            >
              Clone
            </MenuItem>
            <MenuItem
              disabled={isDeleting || !canEdit}
              onClick={async (event) => {
                event.stopPropagation();
                if (isDeleting) {
                  return;
                }
                try {
                  setIsDeleting(true);
                  await onDelete?.(workspaceLayoutPreset);
                } finally {
                  setIsDeleting(false);
                  setMenuAnchorEl(null);
                }
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>
    </WorkspaceLayoutMenuItemWrapper>
  );
};
